import { Icon, SplitRow, FilterControl, DismissPill } from '../common';
import { TYPE_ALL, TYPE_OPTIONS } from './constants';
import { listKindOf, statusOptionsFor, statusSurvives, windowLabel } from './filterTypes';
import { fmtDate } from '../../lib/dates';
import type { ListKind } from '@bbbk/schema';
import type { BookingsFilter, BookingStatus } from './types';

export interface BookingsToolbarProps {
  filter: BookingsFilter;
  setFilter: (f: BookingsFilter) => void;
  /** Today, ISO — for the Date control's honest label. */
  today: string;
}

// The Type filter is single-select with an All row (Sir, 2026-09-02): the
// chosen key becomes the one-kind set, or the empty set for All. A status
// survives the change only where the new kind owns it — All owns both (AC8b).
// A FILTER ONLY ONE READ UNDERSTANDS DOES NOT SURVIVE A SWITCH TO THE OTHER
// (codex, PR #181): the Date window rides the mixed read and clears on a switch
// to Booking, whose untouched list cannot carry it; the In-on occupancy day is
// a bookings question and clears on a switch away from Booking. Otherwise a
// pill would claim a scope the list beneath it was not honouring.
export function onTypeChange(filter: BookingsFilter, key: string | undefined): BookingsFilter {
  const types: ListKind[] = key === undefined || key === TYPE_ALL ? [] : [key as ListKind];
  const booking = key === 'booking';
  return {
    ...filter,
    types,
    status: statusSurvives(filter.status, types) ? filter.status : null,
    window: booking ? null : filter.window,
    inOn: booking ? filter.inOn : null,
  };
}

// The Type filter (AC9), the Status filter that follows it (AC10), the Date
// window handed over by Daily (AC11), and the "In on" occupancy chip. Per-page
// search is GONE (AC3 of search-bookings) — the one global top-bar search
// covers dogs/owners/bookings.
export function BookingsToolbar({ filter, setFilter, today }: BookingsToolbarProps) {
  const kind = listKindOf(filter.types);
  return (
    <SplitRow
      right={
        <>
          <FilterControl
            label="Type"
            icon="list"
            mode="single"
            clearable={false}
            allKey={TYPE_ALL}
            options={TYPE_OPTIONS}
            selected={[kind === 'all' ? TYPE_ALL : kind]}
            onChange={(keys) => setFilter(onTypeChange(filter, keys[0]))}
          />
          {/* Inapplicable while the Date window is present, rather than absent:
              the pill holds its place so nothing shifts, and wakes when the
              window is dismissed (Sir, 2026-09-02). Under All it offers both
              vocabularies — a status belongs to one kind. */}
          <FilterControl
            label="Status"
            icon="circle-check"
            mode="single"
            options={statusOptionsFor(filter.types)}
            selected={filter.status ? [filter.status] : []}
            onChange={(keys) => setFilter({ ...filter, status: (keys[0] as BookingStatus) ?? null })}
            disabled={filter.window !== null}
            disabledHint="Clear the date window to filter by status"
          />
          {filter.window && (
            <DismissPill
              label="Date"
              icon="calendar"
              value={windowLabel(filter.window, today)}
              onDismiss={() => setFilter({ ...filter, window: null })}
            />
          )}
          {/* Active occupancy filter (from "In residence → view all") — shows the
              day and clears on click, so the date-scoped list is never a dead end. */}
          {filter.inOn && (
            <button
              type="button"
              className="filter-chip on"
              onClick={() => setFilter({ ...filter, inOn: null })}
              title="Clear day filter"
            >
              In on {fmtDate(filter.inOn)} <Icon name="close" size={12} />
            </button>
          )}
        </>
      }
    />
  );
}
